import { useMutation, useSubscription } from '@apollo/client'
import { captureException, withErrorBoundary } from '@sentry/react'
import { Popover, Spin } from 'antd'
import moment from 'moment-timezone'
import { useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import NotificationBell from 'src/assets/bell_icon.svg'
import SentryFallback from 'src/components/common/SentryFallback'
import UserDetailsContext from 'src/context/userDetailsContext'
import { UPDATE_USER_NOTIFICATION_DATA } from 'src/schema/mutations'
import { GET_NOTIFICATION_LOGS } from 'src/schema/subscriptions'
import { useTimezone } from 'src/utils/hooks/shared'
import logger from 'src/utils/logger'

import './header.css'

const MenuContent = ({ notificationLogsData }) => {
    const [timezone, timezoneAbbr] = useTimezone()

    const history = useHistory()

    if (notificationLogsData.loading) {
        return <Spin />
    }

    return (
        <div className="max-h-[200px] w-[500px] overflow-y-auto">
            {notificationLogsData.notifications.length > 0 ? (
                notificationLogsData.notifications.map((item) => (
                    <div
                        key={item.id}
                        className="flex cursor-pointer border-b border-neutral-gray-500 pt-2 pb-2"
                        onClick={() => {
                            history.push(item.displayContent.onAction)
                        }}
                    >
                        <div className="pt-1">
                            <img
                                className="my-auto h-5 w-5"
                                src={
                                    item.displayContent?.logo ||
                                    'https://orna-wembleypartners.s3.us-east-2.amazonaws.com/new+icon/icon-info.svg'
                                }
                                alt="notification-logo"
                            />
                        </div>
                        <div className="w-full pl-4 text-white">
                            <div>{item.displayContent?.content}</div>
                            <div className="font-bold text-neutral-gray-75">
                                {moment
                                    .unix(item.created_at)
                                    .tz(timezone)
                                    .format('ddd, MMM D, YYYY h:mm A')}{' '}
                                {timezoneAbbr ? `(${timezoneAbbr})` : ''}
                            </div>
                        </div>
                    </div>
                ))
            ) : (
                <div>No notifications recorded</div>
            )}
        </div>
    )
}

const Notifications = () => {
    const { userDetails } = useContext(UserDetailsContext)

    const [updateUserNotificationData] = useMutation(
        UPDATE_USER_NOTIFICATION_DATA
    )

    const { data: notificationLogsData, error: notificationLogsError } =
        useSubscription(GET_NOTIFICATION_LOGS, {
            variables: {
                userId: userDetails?.id,
                organizationId: userDetails?.organizationId,
            },
            onError: (error) => {
                const errMessage =
                    'An error occurred while fetching from notifications_notificationLogs'
                logger.error(errMessage)
                logger.error(error)
                captureException(error, {
                    extra: { message: { content: errMessage } },
                })
            },
        })

    const unreadNotificationsCount = useMemo(() => {
        let userLastSeenNotification = userDetails?.lastSeenNotificationId
        let counter = 0
        let unreadCount = 0
        let lastSeenNotificationFound = false

        notificationLogsData?.notifications &&
            notificationLogsData?.notifications.forEach((item) => {
                if (unreadCount === 0) {
                    if (userLastSeenNotification === item.id) {
                        unreadCount = counter
                        lastSeenNotificationFound = true
                    } else {
                        counter = counter + 1
                    }
                }
            })

        if (counter > 0 && unreadCount <= 0 && !lastSeenNotificationFound) {
            unreadCount = counter
        }

        return unreadCount
    }, [
        notificationLogsData?.notifications,
        userDetails?.lastSeenNotificationId,
    ])

    if (notificationLogsError) {
        return (
            <div className="cursor-not-allowed text-white">
                <img alt="bell" src={NotificationBell} className="h-4 w-4" />
            </div>
        )
    }

    return (
        <div className="relative mr-2 inline-flex cursor-pointer">
            <Popover
                content={
                    <MenuContent notificationLogsData={notificationLogsData} />
                }
                trigger="click"
            >
                <div
                    onClick={() => {
                        if (notificationLogsData?.notifications.length > 0) {
                            updateUserNotificationData({
                                variables: {
                                    userId: userDetails.id,
                                    lastSeenNotificationId:
                                        notificationLogsData?.notifications[0]
                                            .id,
                                },
                            })
                        }
                    }}
                >
                    <img
                        alt="bell"
                        src={NotificationBell}
                        className="h-5 w-5"
                    />
                    {unreadNotificationsCount > 0 && (
                        <div className="absolute bottom-2 left-2 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                            {unreadNotificationsCount}
                        </div>
                    )}
                </div>
            </Popover>
        </div>
    )
}

export default withErrorBoundary(Notifications, {
    fallback: <SentryFallback />,
})
